/*global SunergoPlatform*/

const mbInBinary = 1048576;
const kbInBinary = 1024;

let tus = false;

const displayFilesize = bytes => {
	if (bytes > mbInBinary) return `${(bytes / mbInBinary).toFixed(2)} MB`;
	if (bytes > kbInBinary) return `${(bytes / kbInBinary).toFixed(2)} KB`;
	return `${bytes} B`;
};

/**
 * Initialize File Upload fields.
 * @param {object} form - the form item.
 * @returns early if there are no File Upload fields.
 */
const initFileUpload = function(form) {
	let fileUploadFields = form.element.querySelectorAll('[data-file-upload]');
	if (!fileUploadFields.length) return;

	/**
	 * Get the file extension from the name of a file.
	 * @param {string} fileName - the name of the file with extension.
	 * @returns the file extension
	 */
	function getFileExtension(fileName) {
		fileName = encodeURIComponent(fileName).toLowerCase();
		return fileName.slice(((fileName.lastIndexOf('.')) >>> 0) + 1) || fileName;
	}

	/**
	 * Initialize file upload fields.
	 * @param {object} field - the file upload field
	 */
	function initField(field) {
		// fileField: field.querySelector('[type="file"]'),
		// fileList: field.querySelector('[data-files-list]'),
		const item = {
			id: field.dataset.fieldId,
			container: field,
			multiple: false,
			fileItemTemplate: 'files-list-item',
			dragCounter: 0
		};

		/**
		 * Setup item field variables.
		 */
		item.setup = function() {
			this.fileField = this.container.querySelector('[type="file"]');
			this.fileList = this.container.querySelector('[data-files-list]');
			this.multiple = this.fileField.multiple;
			this.removeFilesButton = this.container.querySelector('[data-action="remove-files"]');
			this.asyncUpload = this.container.dataset.asyncUpload;
			this.tusResumable = this.container.dataset.tusResumable;

			// Accepts list is a comma separated list of file extensions.
			if (this.fileField.accept) this.accept = this.fileField.accept.split(', ');

			// Max file size is in Megabytes.
			if (this.fileField.dataset.maxFileSize)
				this.maxFileSize = parseInt(this.fileField.dataset.maxFileSize) * mbInBinary;
		};

		/**
		 * Track how many times mouse has dragged over field.
		 * @param {integer} amount - amount to add or subtract to dragCounter
		 */
		item.setDragCounter = function(amount) {
			this.dragCounter = amount == 0 ? 0 : this.dragCounter + amount;
			if (amount > 0) {
				this.container.dataset.dragover = true;
			} else if (this.dragCounter === 0) this.container.dataset.dragover = false;
		};

		/**
		 * Update the field's file list.
		 * @param {object} files - files to be added.
		 * @returns early if there are no files in list.
		 */
		item.updateFileList = function(files) {
			// Clear the file list.
			this.fileList.innerHTML = '';

			// Empty the value if no files in list.
			if (!files || files.length < 1) {
				this.fileField.value = '';
				this.container.dataset.hasFiles = false;
				return;
			}

			const numFiles = this.multiple ? files.length : 1;
			for (let i = 0; i < numFiles; i++) {
				// Get file name, encoding spaces along the way.
				const fileName = encodeURIComponent(files[i].name);
				const fileNameDisplay = fileName.replaceAll('%20', ' ');

				// Create file list item using a template.
				const item = SunergoPlatform.templates.clone(this.fileItemTemplate);

				// Insert the file name.
				item.title = fileNameDisplay;
				const fileElement = item.getElementsByClassName('files-list-item__name')[0];
				fileElement.textContent = fileNameDisplay;

				// Insert file size.
				const fileSize = files[i].size;
				const sizeDisplay = ` (${displayFilesize(fileSize)})`;
				const sizeElement = document.createElement('small');
				sizeElement.textContent = sizeDisplay;
				fileElement.appendChild(sizeElement);

				// Create a thumbnail.
				let thumbnail;
				const fileExt = getFileExtension(fileName);
				const reader = new FileReader();
				switch (fileExt) {
					case 'jpg':
					case 'jpeg':
					case 'png':
					case 'gif':
						// Use the image if its an image.
						thumbnail = document.createElement('img');
						reader.onload = function(e) {
							thumbnail.src = e.target.result;
							thumbnail.alt = 'Preview of image to be uploaded.';
							thumbnail.draggable = false;
						};
						reader.readAsDataURL(files[i]);
						break;

					default:
						// Otherwise, show the file type.
						thumbnail = document.createTextNode(fileExt);
				}
				item.getElementsByClassName('files-list-item__thumbnail')[0].appendChild(thumbnail);

				// Add the item to the file list.
				this.fileList.appendChild(item);
			}
			this.container.dataset.hasFiles = true;
		};

		item.checkFileAccepted = function(files) {
			if (item.fileRejectedMsg) form.fieldFeedback.set(this.container.dataset.fieldId);

			item.fileRejectedMsg = false;
			for (let i = 0; i < files.length; i++) {
				// Check file extension.
				const fileExt = '.' + getFileExtension(encodeURIComponent(files[i].name));
				if (this.accept && !this.accept.includes(fileExt)) {
					item.fileRejectedMsg = this.container.dataset.i18nNotAcceptedType;
					break;
				}

				// Check file size.
				if (this.maxFileSize && files[i].size > this.maxFileSize) {
					item.fileRejectedMsg = this.container.dataset.i18nNotAcceptedSize;
					break;
				}
			}
			if (!item.fileRejectedMsg) return files;

			// If no files were accepted, show an error.
			form.fieldFeedback.set(
				this.container.dataset.fieldId,
				'error',
				item.fileRejectedMsg
			);
			return false;
		};

		/**
		 * Initialize the event listeners for the file field.
		 */
		item.addListeners = function() {
			const item = this;
			this.fileField.addEventListener('change', function() {
				item.updateFileList(item.checkFileAccepted(this.files));
			});
			this.removeFilesButton.addEventListener('click', function() {
				item.updateFileList(false);
			});
			this.container.addEventListener('dragenter', function(e) {
				e.stopPropagation();
				e.preventDefault();
				item.setDragCounter(+1);
			});
			this.container.addEventListener('dragleave', function() {
				item.setDragCounter(-1);
			});
			this.container.addEventListener('dragover', function(e) {
				e.stopPropagation();
				e.preventDefault();
			});
			this.container.addEventListener('drop', function(e) {
				e.stopPropagation();
				e.preventDefault();

				const dtFiles = e.dataTransfer.files;
				item.fileField.files = dtFiles;

				item.updateFileList(item.checkFileAccepted(dtFiles));
				item.setDragCounter(0);
			});
		};

		let filesUploaded = -1;
		let uploadPercent = 0;
		const dialogUploadProgress = SunergoPlatform.dialogs.get(`${form.name}_upload`);

		const nativeUploader = function({ numFiles, formSubmit }) {
			const reader = new FileReader();
			const xhr = new XMLHttpRequest();

			let currentPercent = 0;
			xhr.upload.addEventListener('progress', function(e) {
				if (!e.lengthComputable) return;
				uploadPercent = uploadPercent - currentPercent;
				currentPercent = Math.round((e.loaded * 100) / e.total);
				uploadPercent = uploadPercent + currentPercent;
				dialogUploadProgress.setProgress(uploadPercent);
			}, false);

			xhr.upload.addEventListener('load', function() {
				dialogUploadProgress.setProgress(101);
			}, false);

			xhr.open('POST', item.asyncUpload);
			xhr.onreadystatechange = function() {
				if (xhr.readyState != 4 || xhr.status != 200) return false;
				filesUploaded ++;
				if (filesUploaded < numFiles) return false;
				formSubmit();
				return true;
			};
			xhr.overrideMimeType('text/plain; charset=x-user-defined-binary');

			reader.onload = function(evt) {
				xhr.send(evt.target.result);
			};

			for (let i = 0; i < numFiles; i++)
				reader.readAsBinaryString(item.fileField.files[i]);

			return false;
		};

		const tusUploader = function({ numFiles, formSubmit }) {
			if (!tus) tus = require('tus-js-client');

			let currentPercent = 0;

			for (let i = 0; i < numFiles; i++) {
				const file = item.fileField.files[i];

				const upload = new tus.Upload(file, {
					endpoint: item.asyncUpload,
					// retryDelays: [0, 3000, 5000, 10000, 20000],
					retryDelays: [0, 3000],
					metadata: {
						filename: file.name,
						filetype: file.type
					},
					onError: function(error) {
						dialogUploadProgress.hide();
						console.error(error);
						form.fieldFeedback.set(item.id, 'error', 'Failed to upload. Please try again.');
						form.processing.set(false);
						filesUploaded = -1;
						return false;
					},
					onProgress: function(bytesUploaded, bytesTotal) {
						uploadPercent = uploadPercent - currentPercent;
						currentPercent = Math.round(bytesUploaded / bytesTotal * 100);
						uploadPercent = uploadPercent + currentPercent;
						dialogUploadProgress.setProgress(uploadPercent);
					},
					onSuccess: function() {
						filesUploaded ++;
						if (filesUploaded >= numFiles) {
							dialogUploadProgress.setProgress(101);
							formSubmit();
							return true;
						}
					}
				});

				upload.findPreviousUploads().then(function (previousUploads) {
					if (previousUploads.length)
						upload.resumeFromPreviousUpload(previousUploads[0]);
					upload.start();
				});
			}

			return false;
		};

		item.asyncUploader = function(formSubmit) {
			if (filesUploaded == 0) return false;

			if (filesUploaded < 0) filesUploaded = 0;
			dialogUploadProgress.show();

			const numFiles = item.fileField.files.length;
			if (numFiles < 1) return true;

			if (item.tusResumable) tusUploader({ numFiles, formSubmit });
			else nativeUploader({numFiles, formSubmit});

			return false;
		};

		item.setup();
		item.addListeners();

		if (item.asyncUpload && item.asyncUpload != '') {
			if (!form.asyncUpload) form.asyncUpload = [];
			form.asyncUpload.push(item.asyncUploader);
		}
	}

	fileUploadFields.forEach(function(item) {
		initField(item);
	});
};

export default initFileUpload;